import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink, Download, BarChart3 } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { BenchmarksKpiRail } from '@/components/benchmarks/BenchmarksKpiRail'
import { NotesPanel } from '@/components/shared/NotesPanel'
import {
  useBenchmarksList,
  type BenchmarkPublicFilters,
} from '@/hooks/useBenchmarkQueries'
import { useFilterDefaultsFromPrefs } from '@/hooks/useFilterDefaultsFromPrefs'
import { formatDate } from '@/lib/utils'

/**
 * Wave-1 A1e — Benchmarks list surface.
 *
 * Renders a filter rail (publisherSlug + publisherType + reportType + topic)
 * above a KPI rail and the catalogue grid. The same filter object feeds BOTH
 * the `/stats` endpoint (KPI rail) and the `/benchmarks` list — that is the
 * stats/list alignment guarantee. The fix in `apps/api/src/routes/benchmarks.routes.ts`
 * adds `isNull(suppressedAt)` to the list `where` (matching the existing
 * `/stats` clause), so the count above always equals the visible row count
 * for the same filter window.
 *
 * P5 adoption — `useFilterDefaultsFromPrefs<BenchmarkFilters>('benchmarks')`
 * is invoked on mount per `filter-rail-must-adopt-prefs` ESLint rule (see
 * `docs/conventions/list-endpoints.md` rule 4). When the user has a saved
 * search named `'benchmarks'` we hydrate filter state from it; otherwise
 * the rail starts empty (global view per Captain Input #4).
 */
export function BenchmarksPage() {
  // P5 adoption — required by `filter-rail-must-adopt-prefs` ESLint rule.
  const { defaults } = useFilterDefaultsFromPrefs<BenchmarkPublicFilters>(
    'benchmarks',
  )
  const [filters, setFilters] = useState<BenchmarkPublicFilters>(
    defaults ?? {},
  )

  // Wave 2 API returns `{ items, total }`; the list endpoint applies
  // `status='published' AND suppressed_at IS NULL` server-side so no
  // client-side defensive filtering is needed.
  const { data, isLoading } = useBenchmarksList(filters)

  const all = data?.items ?? []

  // Build the filter dimensions consumed by both the KPI rail (/stats) and
  // the list query — alignment guarantee (Wave-1 A1e).
  const sharedFilters = {
    publisherType: filters.publisherType,
    reportType: filters.reportType,
    topic: filters.topic,
    publisherSlug: filters.publisherSlug,
  }

  const handleFilterChange = (
    field: keyof BenchmarkPublicFilters,
    value: string,
  ) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value === '' ? undefined : value,
    }))
  }

  const handleClear = () => {
    setFilters({})
  }

  const hasActiveFilter =
    !!filters.publisherSlug ||
    !!filters.publisherType ||
    !!filters.reportType ||
    !!filters.topic

  return (
    <main className="flex flex-col gap-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold">Benchmarks</h1>
        <p className="text-muted-foreground">
          Industry benchmarks, state-of-industry reports, and key statistics.
        </p>
      </header>

      {/*
        Wave-1 A1e — filter rail. Mirrors the dimensions accepted by both
        `/api/benchmarks` (list) and `/api/benchmarks/stats` (KPI rail) so
        the count above always agrees with the visible catalogue below.
      */}
      <section
        data-testid="benchmarks-filter-rail"
        aria-label="Benchmark filters"
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-4"
      >
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-medium text-muted-foreground">Publisher</span>
          <input
            type="text"
            data-testid="filter-publisher-slug"
            placeholder="e.g. gartner"
            value={filters.publisherSlug ?? ''}
            onChange={(e) => handleFilterChange('publisherSlug', e.target.value)}
            className="h-9 w-48 rounded-md border bg-background px-2 text-sm"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-medium text-muted-foreground">
            Publisher type
          </span>
          <select
            data-testid="filter-publisher-type"
            value={filters.publisherType ?? ''}
            onChange={(e) =>
              handleFilterChange('publisherType', e.target.value)
            }
            className="h-9 w-40 rounded-md border bg-background px-2 text-sm"
          >
            <option value="">All</option>
            <option value="analyst">Analyst</option>
            <option value="vendor">Vendor</option>
            <option value="research_firm">Research firm</option>
            <option value="consortium">Consortium</option>
            <option value="government_agency">Government</option>
            <option value="other">Other</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-medium text-muted-foreground">Report type</span>
          <input
            type="text"
            data-testid="filter-report-type"
            placeholder="e.g. annual"
            value={filters.reportType ?? ''}
            onChange={(e) => handleFilterChange('reportType', e.target.value)}
            className="h-9 w-40 rounded-md border bg-background px-2 text-sm"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-medium text-muted-foreground">Topic</span>
          <input
            type="text"
            data-testid="filter-topic"
            placeholder="e.g. ransomware"
            value={filters.topic ?? ''}
            onChange={(e) => handleFilterChange('topic', e.target.value)}
            className="h-9 w-40 rounded-md border bg-background px-2 text-sm"
          />
        </label>
        {hasActiveFilter && (
          <button
            type="button"
            data-testid="filter-clear"
            onClick={handleClear}
            className="h-9 rounded-md border bg-background px-3 text-sm hover:bg-muted"
          >
            Clear
          </button>
        )}
      </section>

      {/*
        SP5 Wave 3 — KPI rail is fed from `/api/benchmarks/stats`. With the
        Wave-1 A1e alignment patch the KPI counts always agree with the
        visible catalogue under the same filter window.
      */}
      <BenchmarksKpiRail filters={sharedFilters} />

      <section>
        {isLoading ? (
          <div
            data-testid="benchmarks-list"
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-lg border bg-card p-4 space-y-2">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-3 w-2/3" />
              </div>
            ))}
          </div>
        ) : all.length === 0 ? (
          <p
            data-testid="benchmarks-list-empty"
            className="text-muted-foreground text-sm"
          >
            No benchmarks available.
          </p>
        ) : (
          <div
            data-testid="benchmarks-list"
            data-total={data?.total ?? all.length}
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {all.map((b) => (
              <div
                key={b.id}
                data-testid="benchmark-card"
                className="flex flex-col rounded-lg border bg-card p-4"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <BarChart3 className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-sm leading-snug mb-0.5 line-clamp-2">
                      <Link
                        to={`/benchmarks/${b.id}`}
                        className="hover:underline focus-visible:underline"
                      >
                        {b.title}
                      </Link>
                    </h3>
                    <p className="text-xs text-muted-foreground">{b.publisher}</p>
                  </div>
                </div>

                {/* Key metric */}
                <div className="rounded-md bg-primary/5 px-3 py-2 mb-3">
                  <p className="text-xs font-medium text-muted-foreground mb-0.5">
                    Key metric
                  </p>
                  <p className="text-sm font-semibold text-primary">
                    {b.keyMetric}
                  </p>
                </div>

                <p className="text-xs text-muted-foreground line-clamp-3 mb-3 flex-1">
                  {b.description}
                </p>

                <div className="flex items-center justify-between mt-auto">
                  <span className="text-xs text-muted-foreground">
                    {formatDate(b.publishedAt)}
                  </span>
                  <div className="flex items-center gap-2">
                    {b.publisherUrl && (
                      <a
                        href={b.publisherUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1 text-xs text-primary hover:underline"
                        aria-label={`${b.publisher} website`}
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                    <a
                      href={b.reportUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <Download className="h-3 w-3" />
                      Download
                    </a>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t">
                  <NotesPanel entityType="benchmark" entityId={b.id} />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  )
}
